import React from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { setSession } from "../../utils/jwt";

import {
  Grid,
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
  Button,
} from "@mui/material";

import { Menu as MenuIcon, Warning as WarningIcon } from "@mui/icons-material";

import NavbarUserDropdown from "./NavbarUserDropdown";

// Styled components
const AppBar = styled(MuiAppBar)`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

const ImpersonationBanner = styled("div")`
  background: linear-gradient(90deg, #ff9800, #f57c00);
  color: white;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const Navbar = ({ onDrawerToggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isImpersonating, user } = useAuth();

  const handleExitImpersonation = () => {
    const adminToken = window.localStorage.getItem("adminToken");
    const adminPermissions = window.localStorage.getItem("adminPermissions");

    // Safety check - if no admin token, clear everything and redirect to login
    if (!adminToken) {
      window.localStorage.removeItem("adminToken");
      window.localStorage.removeItem("adminPermissions");
      window.localStorage.removeItem("accessToken");
      window.localStorage.removeItem("rbacPermissions");
      window.location.href = "/auth/sign-in";
      return;
    }

    // Restore admin session
    setSession(adminToken);

    // Restore admin permissions
    if (adminPermissions) {
      window.localStorage.setItem("rbacPermissions", adminPermissions);
    }

    // Clean up impersonation data
    window.localStorage.removeItem("adminToken");
    window.localStorage.removeItem("adminPermissions");
    window.localStorage.removeItem("impersonatedUser");

    // Force full page reload to reset all state
    window.location.href = "/private";
  };

  return (
    <React.Fragment>
      {isImpersonating && (
        <ImpersonationBanner>
          <WarningIcon />
          <span>Impersonating: {user?.name || "User"}</span>
          <Button
            variant="contained"
            size="small"
            onClick={handleExitImpersonation}
            sx={{
              backgroundColor: "white",
              color: "#f57c00",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            Switch Back
          </Button>
        </ImpersonationBanner>
      )}
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Grid container alignItems="center">
            <Grid item sx={{ display: { xs: "block", md: "none" } }}>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onDrawerToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid item xs />
            <Grid item>
              <NavbarUserDropdown />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default withTheme(Navbar);
